import React from 'react';
import { Form } from 'react-bootstrap';
import Alert from 'react-bootstrap/Alert';
import Card from 'react-bootstrap/Card';
import Select from 'react-select';

import { Option } from '../Selects/Selector';
import CompanySelector from '../Selects/CompanySelector';
import TeamSelector from '../Selects/TeamSelector';
import Button from '../../../common/components/Button/Button';
import FormRow from '../../../app/components/FormRow';
import { NewDateRangePicker } from '../../../app/components/NewDateRangePicker';
import { FiltrYearTypeEnum } from '../../../common/pokCore/validation/enums';
import { useTranslation } from '../../../common/hooks/useTranslation';
import { usePokCore } from '../../../common/hooks/usePokCore';

import type { FilterYearProps } from './types/FilterYearTypes';
import { useFilterYearState } from './hooks/useFilterYearState';
import { getDateValue, createSelectOptions } from './utils/filterYearUtils';
import { FilterYearBasicFields } from './FilterYearBasicFields';
import { FilterYearAdvancedFields } from './FilterYearAdvancedFields';

export const FilterYear: React.FC<FilterYearProps> = props => {
  const pok = usePokCore();
  const { t, tk } = useTranslation('reportLng');

  const state = useFilterYearState(props.withYearType);
  const { yearTypeOptions } = createSelectOptions(t);

  const handleSearch = () => {
    if (!state.year) {
      state.setShowNewAlert(true);
      return;
    }
    state.setShowNewAlert(false);

    props.onSearch({
      year: state.year,
      companyId: props.withoutCompany ? pok.getCompanyId() : state.companyId,
      type: state.type,
      budget: state.budget,
      currency: state.currency,
      clientId: state.clientId,
      saleDateFrom: getDateValue(state.saleDate, 0),
      saleDateTo: getDateValue(state.saleDate, 1),
      invoiceDateFrom: getDateValue(state.invoiceDate, 0),
      invoiceDateTo: getDateValue(state.invoiceDate, 1),
      yearType: state.yearType,
      status: state.status,
      teamId: state.teamId,
      rangeMonthFrom: getDateValue(state.rangeMonth, 0),
      rangeMonthTo: getDateValue(state.rangeMonth, 1),
      month: state.month,
      invoiced: state.invoiced,
      salesInvoices: state.salesInvoices,
      salesInvoicesCopies: state.salesInvoicesCopies,
      purchaseInvoices: state.purchaseInvoices,
      contracts: state.contracts,
      projectAttachments: state.projectAttachments,
      project: state.project,
      attachmentsTypesIds: state.attachmentsTypesIds,
    });
  };

  return (
    <Card className="mb-2">
      <Card.Header>{props.reportName}</Card.Header>
      <Card.Body>
        <Form className="d-grid gap-3">
          {props.withYear && (
            <FormRow controlId="year" label={t(tk.common.year)}>
              <Select
                value={state.yearsOptions.find(
                  ({ value }) => value === state.year,
                )}
                options={state.yearsOptions}
                onChange={option => state.setYear(Number(option?.value))}
                placeholder={t(tk.component.pointOut)}
              />
            </FormRow>
          )}

          {props.withYearType && (
            <FormRow controlId="yearType" label={t(tk.finance.yearType)}>
              <Select
                value={
                  state.yearType
                    ? yearTypeOptions.find(
                        ({ value }) => state.yearType === value,
                      )
                    : null
                }
                options={
                  props.withoutDateOfProject
                    ? yearTypeOptions.filter(
                        o => o.value !== FiltrYearTypeEnum.DateOfProject,
                      )
                    : yearTypeOptions
                }
                onChange={option => state.setYearType(option?.value)}
                placeholder={t(tk.component.pointOut)}
              />
            </FormRow>
          )}

          {props.withRangeMonth && (
            <FormRow controlId="rangeMonth" label={t(tk.filter.rangeMonth)}>
              <div className="small-calendar">
                <NewDateRangePicker
                  onChange={value =>
                    state.setRangeMonth(
                      value as unknown as [Date | null, Date | null] | null,
                    )
                  }
                  value={state.rangeMonth}
                />
              </div>
            </FormRow>
          )}

          {!props.withoutCompany && (
            <FormRow controlId="company" label={t(tk.common.company)}>
              <CompanySelector
                value={state.companyId}
                onChange={option => {
                  if (!(option as Option)?.value) {
                    state.setTeamId(undefined);
                    state.setProject(undefined);
                  }
                  state.setCompanyId((option as Option)?.value);
                }}
                clearable={true}
              />
            </FormRow>
          )}

          {props.withTeam && (
            <FormRow controlId="teamId" label={t(tk.common.team)}>
              <TeamSelector
                value={state.teamId}
                companyId={
                  props.withoutCompany ? pok.getCompanyId() : state.companyId
                }
                onChange={option => state.setTeamId((option as Option)?.value)}
                clearable={true}
              />
            </FormRow>
          )}

          <FilterYearBasicFields props={props} state={state} t={t} tk={tk} />
          <FilterYearAdvancedFields props={props} state={state} t={t} tk={tk} />
        </Form>

        {state.showNewAlert && (
          <Alert variant="danger" className="mt-3">
            {t(tk.filter.yearGive)}
          </Alert>
        )}

        <div className="d-flex justify-content-end mt-3">
          <Button
            className="me-2"
            variant="outline-dark"
            onClick={state.clearFilters}
          >
            {t(tk.button.clean)}
          </Button>
          <Button
            variant="outline-primary"
            onClick={handleSearch}
            isLoading={props.isLoading}
          >
            {t(tk.button.export)}
          </Button>
        </div>
      </Card.Body>
    </Card>
  );
};
